import { read_session_project_id } from '../CompanyCases/company_cases_common';
import {
  billable_report_description,
  billable_report_sections,
  billable_summary_rows,
} from './company_reports_billable_helpers';
import { audit_report_row, case_report_row } from './company_reports_download_helpers';
import {
  load_report_enrichments,
  load_report_sources,
  report_load_error,
} from './company_reports_source_helpers';

export const REPORT_FILTERS = [
  { key: 'all', label: 'All reports' },
  { key: 'operational', label: 'Operational' },
  { key: 'financial', label: 'Financial' },
  { key: 'compliance', label: 'Compliance' },
];

export const REPORT_DATE_RANGE_OPTIONS = [
  { value: 'last-90-days', label: 'Last 90 days' },
  { value: 'last-30-days', label: 'Last 30 days' },
  { value: 'quarter-to-date', label: 'Quarter to date' },
];

export const REPORT_SCOPE_OPTIONS = [
  { value: 'my-caseload', label: 'My caseload' },
  { value: 'high-risk-only', label: 'High-risk only' },
  { value: 'my-domain-mix', label: 'My domain mix' },
];

export const REPORT_FORMAT_OPTIONS = [
  { value: 'pdf', label: 'PDF', description: 'Boardroom layout' },
  { value: 'xlsx', label: 'XLSX', description: 'Pivots and charts' },
  { value: 'csv', label: 'CSV', description: 'Raw rows' },
];

const CATEGORY_BADGES = new Map([
  ['operational', 'bg-success-subtle text-success-emphasis border border-success-subtle'],
  ['financial', 'bg-warning-subtle text-warning-emphasis border border-warning-subtle'],
  ['compliance', 'bg-danger-subtle text-danger-emphasis border border-danger-subtle'],
]);
const DEFAULT_CATEGORY_BADGE = CATEGORY_BADGES.get('operational');

export async function load_company_reports({
  project_id = read_session_project_id(),
  current_role = '',
  can_route_cases = false,
} = {}) {
  return build_company_reports(
    project_params(project_id),
    { project_id, current_role, can_route_cases }
  ).catch((error) => {
    throw report_load_error(error);
  });
}

export function filter_reports(reports = [], active_filter = 'all') {
  return active_filter === 'all'
    ? reports
    : reports.filter((report) => report.category_key === active_filter);
}

async function build_company_reports(params = {}, case_options = {}) {
  const [cases, audits, profiles] = await load_report_sources(params, case_options);
  const [risk_records, billable_rows] = await load_report_enrichments(cases, profiles, params);

  return [
    ...operational_reports(cases),
    ...risk_reports(risk_records),
    ...financial_reports(cases, billable_rows),
    ...compliance_reports(audits),
  ];
}

function project_params(project_id) {
  return project_id ? { project_id } : {};
}

function operational_reports(cases = []) {
  if (!cases.length) return [];

  return [
    report({
      id: 'operational-case-performance',
      category_key: 'operational',
      title: 'Case performance',
      description: `${cases.length} backend case${plural(cases.length)} included across ${domain_count(cases)} domain${plural(domain_count(cases))}`,
      rows: cases.map(case_report_row),
    }),
  ];
}

function risk_reports(risk_records = []) {
  if (!risk_records.length) return [];

  return [
    report({
      id: 'operational-risk-distribution',
      category_key: 'operational',
      title: 'Risk band distribution',
      description: `${risk_records.length} submitted risk assessment${plural(risk_records.length)} recorded from backend risk data`,
      rows: risk_records,
    }),
  ];
}

function financial_reports(cases = [], billable_rows = []) {
  if (!cases.length && !billable_rows.length) return [];

  return [
    report({
      id: 'financial-billable-hours',
      category_key: 'financial',
      title: 'Billable hours by case',
      description: billable_report_description(billable_rows),
      rows: billable_summary_rows(billable_rows),
      sections: billable_report_sections(billable_rows),
    }),
  ];
}

function compliance_reports(audits = []) {
  if (!audits.length) return [];

  return [
    report({
      id: 'compliance-audit-activity',
      category_key: 'compliance',
      title: 'Audit activity log',
      description: `${audits.length} audit event${plural(audits.length)} available for compliance review`,
      rows: audits.map(audit_report_row),
    }),
  ];
}

function report({ id, category_key, title, description, rows = [], sections = [] }) {
  return {
    id,
    category_key,
    category_label: category_label_for(category_key),
    category_badge_class: category_badge_class(category_key),
    title,
    description,
    rows,
    sections,
  };
}

function category_badge_class(category_key = '') {
  return CATEGORY_BADGES.get(category_key) || DEFAULT_CATEGORY_BADGE;
}

function category_label_for(category_key = '') {
  const filter = REPORT_FILTERS.find((item) => item.key === category_key);
  return filter?.label || 'Report';
}

function domain_count(cases = []) {
  return new Set(cases.map((item) => item.domain).filter(Boolean)).size;
}

function plural(count) {
  return count === 1 ? '' : 's';
}
